import { OverlayTrigger } from '@hai/ui-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RecordingIconDot } from 'src/js/component/recording-icon-dot';
import { toTitleCase } from 'src/js/util/global-util';
import { storageStore } from '../recordings/storage-store';

import { getRecordingTooltip, recordIsRunning, streamIsStopped, StreamViewModel } from './stream-models';

export interface StreamRecordingOverlayProps {
  popoverContent: React.ReactNode;
  recordingFinished: boolean;
  recordingName: string;
  statusText: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Recording row: tooltip, click-through to the recording detail page, and trailing status text.
 * Pass the icon (or any leading content) as `children`.
 */
export const StreamRecordingOverlay: React.FC<StreamRecordingOverlayProps> = ({
  popoverContent,
  recordingFinished,
  recordingName,
  statusText,
  children,
}) => {
  const navigate = useNavigate();

  return (
    <OverlayTrigger
      popoverContent={popoverContent}
      className={recordingFinished ? 'cursorPointer' : undefined}
      placement="top"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/media/recordings/${recordingName}`);
      }}
    >
      {'\u00A0'}
      {children}
      {'\u00A0'}
      {statusText}
    </OverlayTrigger>
  );
};

export interface StreamRecordingTitleDotOverlayProps {
  model: StreamViewModel;
  /** Color when `recordIsRunning(model.recording)` is true (icon + status text). */
  activeRecordingColor: string;
  /** Color when recording is not running (icon + status text). */
  inactiveRecordingColor: string;
  dotSize?: string;
  dotClassName?: string;
}

/**
 * Title-bar recording indicator: dot icon, colors from recording state, and shared overlay behavior.
 */
export const StreamRecordingTitleDotOverlay: React.FC<StreamRecordingTitleDotOverlayProps> = ({
  model,
  activeRecordingColor,
  inactiveRecordingColor,
  dotSize = '15px',
  dotClassName = 'recording-overlay',
}) => {
  const { t } = useTranslation();
  const recording = model.recording;

  if (!recording?.name) {
    return null;
  }

  // Compute recording status text
  let recordStatusText = '';
  let diskFull = false;
  if (model?.recording?.enabled === true) {
    if (model.recording.status === 'INITIALIZING') {
      recordStatusText = 'Initializing';
    } else {
      recordStatusText =
        streamIsStopped(model.state) && model.recording.autoStart
          ? t('') //stream.recordAutoStart
          : recordIsRunning(model.recording)
          ? model.recording.limited
            ? `${model.recording.percentageDone}%`
            : model.recording.upTime && model.recording.upTime !== ''
            ? model.recording.upTimeClock
            : 'Initializing'
          : toTitleCase(model.recording.status);
      if (model.recording.segmented && model.recording.recycleSegments) {
        if (recordIsRunning(model.recording)) {
          recordStatusText = model.recording.upTimeClock ?? 'Initializing';
        }
      }
    }

    // if recording status is Halted and disk is currently full, append " - Disk Full"
    if (model.recording?.status === 'HALTED' && storageStore.internal?.total > 0 && storageStore.internal?.free === 0) {
      diskFull = true;
    }
  }

  const running = recordIsRunning(recording);
  const color = running ? activeRecordingColor : inactiveRecordingColor;

  let recTextColor = '#9595a1';
  if (recording.status === 'RECORDING' || recording.status === 'INITIALIZING') {
    recTextColor = '#ea4645';
  }

  return (
    <StreamRecordingOverlay
      popoverContent={diskFull ? t('assets.diskFull') : getRecordingTooltip(model, t)}
      recordingFinished={recording.status === 'FINISHED'}
      recordingName={recording.name}
      statusText={
        <span className="mk-record-overlay-status-text" style={{ color }}>
          {recordStatusText}
        </span>
      }
    >
      <div className="recIcon">
        <RecordingIconDot
          size={dotSize}
          className={dotClassName}
          text="REC"
          textColor={recTextColor}
          model={model}
          running={running}
          diskFull={recordStatusText.includes(t('assets.diskFull'))}
        />
      </div>
    </StreamRecordingOverlay>
  );
};
